import { useContext, useEffect, useState } from 'react';

import { camelize } from '@vimeo/core/libs/camelize';
import { Viewer, ViewerContext } from '@vimeo/viewer';

import { Clip, ClipPlaceholder, LiveChatStatusResponse } from '../types';

const POLLING_DELAY: number = 4000;

/**
 * Using native fetch over vimeo query hooks because caching causes major issues there.
 */
async function fetchLiveChatStatus(roomId: number, viewer: Viewer): Promise<boolean> {
  const response = await fetch(`//${viewer.apiUrl}/live_chat/${roomId}/status`, {
    headers: {
      Authorization: `jwt ${viewer.jwt}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  if (response.ok) {
    const data: LiveChatStatusResponse = camelize(await response.json());
    return data.chatEnabled;
  } else {
    throw new Error('Failed to fetch chat status data.');
  }
}

/**
 * Observe live_chat status in realtime.
 * Chat status is visible when it is (1) enabled and (2) event is ongoing.
 */
export function useLiveChatStatus(clipDTO?: Clip | ClipPlaceholder): [boolean] {
  const viewer: Viewer = useContext(ViewerContext) as Viewer;
  const [isEnabled, setIsEnabled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isStreamingClip: boolean = clipDTO?.live?.status === 'streaming';

  // Poll chat status for live events that are currently streamed.
  useEffect(
    function () {
      if (isStreamingClip) {
        const roomId: number = clipDTO?.live?.chat.roomId as number;
        setTimeout(async () => {
          try {
            const status = await fetchLiveChatStatus(roomId, viewer);
            setIsEnabled(status);
          } catch (error) {
            console.error(error);
          }
        });
        const intervalId = setInterval(async () => {
          try {
            setIsEnabled(await fetchLiveChatStatus(roomId, viewer));
          } catch (error) {
            // Chat will handle itself correctly if it is shown, but disabled previously.
            // setIsEnabled(false);
          }
        }, POLLING_DELAY);

        return () => clearInterval(intervalId);
      } else {
        setIsEnabled(false);
      }
    },
    [isStreamingClip, viewer.jwt],
  );

  useEffect(
    function () {
      setIsVisible(Boolean(isStreamingClip && isEnabled));
    },
    [isStreamingClip, isEnabled],
  );

  return [isVisible];
}
